import React from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from "prop-types";
import { InputLabel, TextField , FormControl, CircularProgress } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { VariableSizeList } from 'react-window';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme } from '@mui/styles';
import { Typography } from '@mui/material';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import { PapiroConsole } from '../../../utils/papiroConsole';


const LISTBOX_PADDING = 8; // px

// Contexto p/ propagar a flag "popper expansivo" do SimpleAutoComplete até o ListboxComponent.
// false (default) → virtualizado, comportamento original. true → render plano, wrap natural.
const ExpandContext = React.createContext(false);


// ============================================================================
// MODO PADRÃO — Listbox virtualizado (react-window). Altura fixa por item.
// ============================================================================

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  if (dataSet.hasOwnProperty('group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const text = dataSet[1].parentname ? dataSet[1].parentname : dataSet[1].name;
  return (
    <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
      {text}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty('group')) return 48;
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) return 8 * itemSize;
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});


// ============================================================================
// MODO EXPANSIVO — Listbox plano (sem virtualização).
// Cada item tem altura natural via `whiteSpace: normal`. O browser cuida.
// Combina com o ExpandingPopper (min-width: max-content) sem cair em cadeia
// circular de larguras — que era o motivo do react-window não conviver com max-content.
// ============================================================================

const PlainListbox = React.forwardRef(function PlainListbox(props, ref) {
  const { children, ...other } = props;
  const items = [];
  children.forEach((item) => {
    items.push(item);
    if (item.children) items.push(...item.children);
  });

  return (
    <ul ref={ref} {...other}>
      {items.map((dataSet, idx) => {
        if (dataSet.hasOwnProperty('group')) {
          return (
            <ListSubheader key={dataSet.key || idx} component="li">
              {dataSet.group}
            </ListSubheader>
          );
        }
        const itemProps = dataSet[0];
        const option = dataSet[1];
        const text = (option && (option.parentname || option.name)) || '';
        return (
          <li
            {...itemProps}
            key={itemProps.key || idx}
            style={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.4,
            }}
          >
            {text}
          </li>
        );
      })}
    </ul>
  );
});


// Dispatcher: hooks chamados no top-level, branch é só no JSX (rules of hooks OK).
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const expand = React.useContext(ExpandContext);
  return expand
    ? <PlainListbox {...props} ref={ref} />
    : <VirtualizedListbox {...props} ref={ref} />;
});

ListboxComponent.propTypes = {
  children: PropTypes.node,
};


const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

// Popper expansivo: width responde sozinho ao conteúdo.
//   - min-width: max-content (!important)  → mínimo = largura do maior item sem wrap
//   - max-width: clamp pra não estourar a viewport
//   - max-height extra pra ver mais itens de uma vez quando lista é grande
// O computed width = max(min-width, inline-width-do-anchor), limitado por max-width.
// Listas curtas → fica na largura do anchor. Listas longas → cresce até o maior item.
// Acima do cap → bate em 520, itens fazem wrap natural (PlainListbox + whiteSpace:normal).
const ExpandingPopper = styled(Popper)({
  minWidth: 'max-content !important',
  maxWidth: 'min(520px, calc(100vw - 24px)) !important',
  [`& .${autocompleteClasses.paper}`]: {
    boxSizing: 'border-box',
  },
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    maxHeight: 'min(60vh, 480px) !important',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});


const useStyles = makeStyles(theme => ({
  listbox: {
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
}));

export default function SimpleAutoComplete(props) {

  const { label, options, changeSelect, optionsText, stateName, onChange, selected, required, disabled, created = false, customRenderOption, htmlOptions = false, loading = false, loadListFunction, expandPopper = false } = props;

  const defaultvalue = { id: 0, name: '' };
  const classes = useStyles();

  const [opt, setOption] = React.useState(selected ? selected : defaultvalue);

  React.useEffect(() => {
    setOption(selected ? selected : defaultvalue);
  }, [selected]);

  const handleChange = (event, value) => {
    if (value == null) {
      value = { id: 0, name: '' };
    }
    setOption(value);
    onChange(value);

    PapiroConsole.log(stateName, value);
    PapiroConsole.log("changeSelect");
    if (changeSelect) {
      changeSelect(stateName, value);
    }
  };

  // Popper expansivo só quando o caller pede; padrão preserva comportamento original.
  const PopperComponent = expandPopper ? ExpandingPopper : StyledPopper;

  function Result() {
    return (
      <ExpandContext.Provider value={!!expandPopper}>
        <Autocomplete
          id="combo-box"
          options={options}
          autoHighlight
          value={opt}
          PopperComponent={PopperComponent}
          ListboxComponent={ListboxComponent}
          getOptionLabel={option => option.parentname ? option.parentname : option.name}
          getOptionDisabled={!created ? option => option.cancreatecalled === false : option => option}
          onChange={handleChange}
          style={{ fullWidth: true }}
          disabled={loading || disabled}
          renderInput={params => (
            <TextField {...params} label={label} variant="outlined" required={required != null ? required == false ? false : true : false} disabled={disabled} fullWidth
              InputProps={(loading != null || typeof loading !== 'undefined') ? {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              } : { ...params.InputProps }} />
          )}
          renderOption={(props, option, state) => [props, option, state.index]}
        />
      </ExpandContext.Provider>
    );
  }

  return (
    <Result />
  );
}

SimpleAutoComplete.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array,
  stateName: PropTypes.string,
  changeSelect: PropTypes.func,
  selected: PropTypes.object,
  checkbox: PropTypes.bool,
  created: PropTypes.bool
};
